import type { UploadedFile } from 'models';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon } from 'ui';
import { useUploadFileMutation } from 'features/files';
import { DropboxChooser } from '../dropbox-chooser';
import { AttachmentMenuFileInput } from './attachment-menu-file-input';

type P = {
  className?: string;
  // TODO: maybe optimize
  onStartUpload: () => void;
  // TODO: this should contain info about progress
  onUpload: (files: UploadedFile[]) => void;
};

const AttachmentMenu = ({ className, onStartUpload, onUpload }: P) => {
  const { t } = useTranslation();
  // TODO: type constants
  const [openedKey, setOpenedKey] = useState<'dropbox'>();
  // TODO: Do not use local loading state
  const [isLoading, setIsLoading] = useState(false);

  const reference = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadFile } = useUploadFileMutation();

  const onLoadingUpdate = (uploading: boolean) => {
    if (uploading) {
      onStartUpload();
    }

    setIsLoading(uploading);
  };

  const onSelectDropbox = async (data: Array<{ link: string; name: string }>) => {
    onLoadingUpdate(true);

    const files = data.map((file) => ({
      name: file.name,
      url: file.link,
    }));

    const uploadedFiles = await Promise.all(
      files.map(async (file): Promise<undefined | UploadedFile> => {
        try {
          return await uploadFile(file);
        } catch {
          return undefined;
        }
      }),
    );

    onUpload(uploadedFiles.filter((file): file is UploadedFile => file !== undefined));

    onLoadingUpdate(false);
  };

  const menu = [
    {
      icon: <Icon icon="computer-line" />,
      key: 'image-upload',
      label: t('Your Computer'),
      onClick() {
        reference.current?.click();
      },
    },
    {
      icon: <Icon icon="dropbox" />,
      key: 'dropbox',
      label: t('Dropbox'),
    },
  ];

  const onSelectKey = ({ key }: { key: string }) => {
    switch (key) {
      case 'dropbox':
        setOpenedKey('dropbox');
        break;
      case 'image-upload':
        break;
      default:
        throw new Error(t('Unsupported option: {{option}}', { option: key }));
    }
  };

  return (
    <>
      <Dropdown
        menu={{
          items: menu,
          onClick(event) {
            onSelectKey(event);
          },
        }}
        trigger={['click']}
      >
        <Button
          aria-label="Attachments"
          className={className}
          icon={
            <>
              <Icon icon="attachment-line" /> <Icon icon="down-small-line" />
            </>
          }
          loading={isLoading}
          type="text"
        />
      </Dropdown>

      <AttachmentMenuFileInput onLoadingUpdate={onLoadingUpdate} onUpload={onUpload} ref={reference} />

      <DropboxChooser
        onDropdownClose={() => {
          setOpenedKey(undefined);
        }}
        onSelect={onSelectDropbox}
        opened={openedKey === 'dropbox'}
      >
        {/* TODO: fix styling */}
        <div style={{ display: 'none' }} />
      </DropboxChooser>
    </>
  );
};

export default AttachmentMenu;
